import React, { useEffect, useState } from "react"
import {
  Table,
  Card,
  Tag,
  Button,
  Space,
  Typography,
  Tooltip,
  message,
  Drawer,
  Descriptions,
  Modal,
  InputNumber,
  Popconfirm,
} from "antd"
import {
  FileTextOutlined,
  DownloadOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  EyeOutlined,
  CheckOutlined,
  CloseOutlined,
} from "@ant-design/icons"
import {
  collection,
  onSnapshot,
  query,
  orderBy,
  doc,
  getDoc,
  updateDoc,
  setDoc,
  serverTimestamp,
} from "firebase/firestore"
import dayjs from "dayjs"
import { db } from "../../firebase/config"
import { useAuthContext } from "../../hooks/useAuthContext"

const { Text } = Typography

export default function ReprintRequestsTable() {
  const [requests, setRequests] = useState([])
  const [loading, setLoading] = useState(true)
  const [selectedRequest, setSelectedRequest] = useState(null)
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [approveModalOpen, setApproveModalOpen] = useState(false)
  const [reprintQuantity, setReprintQuantity] = useState(0)
  const [processing, setProcessing] = useState(false)

  const { admin } = useAuthContext()

  useEffect(() => {
    setLoading(true)
    const q = query(collection(db, "reprintRequests"), orderBy("createdAt", "desc"))

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const data = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }))
        setRequests(data)
        setLoading(false)
      },
      (error) => {
        console.error("Error fetching reprint requests:", error)
        message.error("Failed to load reprint requests")
        setLoading(false)
      }
    )

    return () => unsubscribe()
  }, [])

  const formatDate = (timestamp) => {
    if (!timestamp) return "—"
    const date = timestamp.seconds ? dayjs.unix(timestamp.seconds) : dayjs(timestamp)
    return date.format("DD.MM.YYYY")
  }

  const formatDateTime = (timestamp) => {
    if (!timestamp) return "—"
    const date = timestamp.seconds ? dayjs.unix(timestamp.seconds) : dayjs(timestamp)
    return date.format("DD.MM.YYYY HH:mm")
  }

  const handleDownload = (pdfUrl, actNumber) => {
    if (!pdfUrl) {
      message.error("PDF not available")
      return
    }
    window.open(pdfUrl, "_blank")
  }

  const handleViewDetails = (record) => {
    setSelectedRequest(record)
    setReprintQuantity(record.reprintQuantity || 0)
    setDrawerOpen(true)
  }

  const handleApprove = () => {
    setApproveModalOpen(true)
  }

  const handleDeny = async () => {
    if (!selectedRequest) return

    setProcessing(true)
    try {
      const requestRef = doc(db, "reprintRequests", selectedRequest.id)
      await updateDoc(requestRef, {
        status: "denied",
        deniedAt: serverTimestamp(),
      })

      // Update complaint
      if (selectedRequest.complaintId) {
        const complaintRef = doc(db, "complaints", selectedRequest.complaintId)
        await updateDoc(complaintRef, {
          reprintStatus: "denied",
        })
      }

      message.success("Reprint request denied")
      setDrawerOpen(false)
    } catch (error) {
      console.error("Error denying request:", error)
      message.error("Failed to deny request")
    } finally {
      setProcessing(false)
    }
  }

  const handleConfirmApprove = async () => {
    if (!selectedRequest || !reprintQuantity || reprintQuantity <= 0) {
      message.error("Please enter a valid quantity")
      return
    }

    setProcessing(true)
    try {
      // 1. Get order sequence
      const orderSequenceDocRef = doc(db, "sequence", "orderSequence")
      const orderSequenceSnap = await getDoc(orderSequenceDocRef)

      if (!orderSequenceSnap.exists()) {
        throw new Error("Order sequence document does not exist")
      }

      const currentOrderNumber = orderSequenceSnap.data().number
      const orderPrefix = orderSequenceSnap.data().prefix
      const newOrderNumber = currentOrderNumber + 1

      // 2. Create new order from orderSnapshot
      const orderSnapshot = selectedRequest.orderSnapshot || {}
      const newOrderData = {
        ...orderSnapshot,
        labelCount: reprintQuantity,
        orderNumber: `${orderPrefix}${newOrderNumber}`,
        justOrderNumber: newOrderNumber,
        created: serverTimestamp(),
        orderStatus: 1, // New order status
        isReprint: true,
        originalOrderNumber: selectedRequest.orderNumber,
        reprintFromActNumber: selectedRequest.actNumber,
        reprintRequestId: selectedRequest.id,
      }

      const newOrderRef = doc(collection(db, "orders"))
      await setDoc(newOrderRef, newOrderData)

      // 3. Update order sequence
      await updateDoc(orderSequenceDocRef, {
        number: newOrderNumber,
      })

      // 4. Update reprint request status
      const requestRef = doc(db, "reprintRequests", selectedRequest.id)
      await updateDoc(requestRef, {
        status: "approved",
        approvedAt: serverTimestamp(),
        approvedQuantity: reprintQuantity,
        newOrderNumber: `${orderPrefix}${newOrderNumber}`,
        newOrderId: newOrderRef.id,
      })

      // 5. Update complaint
      if (selectedRequest.complaintId) {
        const complaintRef = doc(db, "complaints", selectedRequest.complaintId)
        await updateDoc(complaintRef, {
          reprintStatus: "approved",
          reprintedOrderNumber: `${orderPrefix}${newOrderNumber}`,
          reprintedAt: serverTimestamp(),
        })
      }

      message.success(`Reprint approved! New order ${orderPrefix}${newOrderNumber} created`)
      setApproveModalOpen(false)
      setDrawerOpen(false)
    } catch (error) {
      console.error("Error approving reprint:", error)
      message.error("Failed to approve reprint: " + error.message)
    } finally {
      setProcessing(false)
    }
  }

  const getStatusTag = (status) => {
    switch (status) {
      case "pending":
        return (
          <Tag icon={<ClockCircleOutlined />} color="processing">
            Pending
          </Tag>
        )
      case "approved":
        return (
          <Tag icon={<CheckCircleOutlined />} color="success">
            Approved
          </Tag>
        )
      case "denied":
        return <Tag color="error">Denied</Tag>
      case "completed":
        return (
          <Tag icon={<CheckCircleOutlined />} color="success">
            Completed
          </Tag>
        )
      default:
        return <Tag>{status}</Tag>
    }
  }

  const getReasonText = (reason, language) => {
    const translations = {
      production: language === "lv" ? "Ražošanas defekts" : "Production defect",
      layout: language === "lv" ? "Izkārtojuma kļūda" : "Layout error",
      clientFault:
        language === "lv"
          ? "Defekts klienta vainas dēļ"
          : "Defect due to client fault (by agreement)",
      other: language === "lv" ? "Cits" : "Other",
    }
    return translations[reason] || reason
  }

  const columns = [
    {
      title: "Act No.",
      dataIndex: "actNumber",
      width: 100,
      render: (v) => <Text strong>#{v}</Text>,
    },
    {
      title: "Date",
      dataIndex: "date",
      width: 120,
      render: (v) => formatDate(v),
      sorter: (a, b) => {
        const dateA = a.date?.seconds || 0
        const dateB = b.date?.seconds || 0
        return dateA - dateB
      },
    },
    {
      title: "Order Number",
      dataIndex: "orderNumber",
      width: 140,
      render: (v) => <Text style={{ color: "#1890ff" }}>{v || "—"}</Text>,
    },
    {
      title: "Product",
      dataIndex: "productName",
      ellipsis: true,
      width: 200,
      render: (v) => (
        <Tooltip title={v}>
          <Text>{v || "—"}</Text>
        </Tooltip>
      ),
    },
    {
      title: "Quantity",
      dataIndex: "reprintQuantity",
      width: 100,
      align: "right",
      render: (v) => <Text>{v || 0}</Text>,
    },
    {
      title: "Reason",
      dataIndex: "complaintReason",
      width: 180,
      render: (v, record) => (
        <Tooltip title={record.otherReasonText || getReasonText(v, record.language)}>
          <Tag color="orange">{getReasonText(v, record.language)}</Tag>
        </Tooltip>
      ),
    },
    {
      title: "Deadline",
      dataIndex: "deadline",
      width: 120,
      render: (v) => formatDate(v),
    },
    {
      title: "Status",
      dataIndex: "status",
      width: 120,
      render: (v) => getStatusTag(v),
      filters: [
        { text: "Pending", value: "pending" },
        { text: "Approved", value: "approved" },
        { text: "Denied", value: "denied" },
        { text: "Completed", value: "completed" },
      ],
      onFilter: (value, record) => record.status === value,
    },
    {
      title: "Created",
      dataIndex: "createdAt",
      width: 150,
      render: (v) => formatDateTime(v),
      sorter: (a, b) => {
        const dateA = a.createdAt?.seconds || 0
        const dateB = b.createdAt?.seconds || 0
        return dateA - dateB
      },
      defaultSortOrder: "descend",
    },
    {
      title: "Actions",
      key: "actions",
      width: 150,
      fixed: "right",
      render: (_, record) => (
        <Space>
          <Tooltip title="View Details">
            <Button size="small" icon={<EyeOutlined />} onClick={() => handleViewDetails(record)} />
          </Tooltip>
          <Tooltip title="Download PDF">
            <Button
              size="small"
              icon={<DownloadOutlined />}
              onClick={() => handleDownload(record.pdfUrl, record.actNumber)}
            />
          </Tooltip>
        </Space>
      ),
    },
  ]

  return (
    <>
      <Card
        title={
          <Space>
            <FileTextOutlined /> Reprint Requests
          </Space>
        }>
        <Table
          rowKey="id"
          columns={columns}
          dataSource={requests}
          loading={loading}
          bordered
          size="small"
          scroll={{ x: 1400 }}
          pagination={{
            pageSize: 50,
            showSizeChanger: true,
            showTotal: (total) => `Total ${total} requests`,
          }}
        />
      </Card>

      {/* Details Drawer */}
      <Drawer
        title={`Reprint Request #${selectedRequest?.actNumber}`}
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        width={720}>
        {selectedRequest && (
          <Space direction="vertical" style={{ width: "100%" }} size="large">
            {/* Status and Actions */}
            <Card size="small" title="Status">
              <Space direction="vertical" style={{ width: "100%" }}>
                <div>{getStatusTag(selectedRequest.status)}</div>

                <Space>
                  <Button
                    type="primary"
                    icon={<CheckOutlined />}
                    onClick={handleApprove}
                    loading={processing}>
                    Approve Reprint
                  </Button>
                  <Popconfirm
                    title="Deny this reprint request?"
                    onConfirm={handleDeny}
                    okText="Yes, Deny"
                    cancelText="Cancel">
                    <Button danger icon={<CloseOutlined />} loading={processing}>
                      Deny
                    </Button>
                  </Popconfirm>
                </Space>

                {selectedRequest.newOrderNumber && (
                  <Text>
                    New Order Created: <Text strong>{selectedRequest.newOrderNumber}</Text>
                  </Text>
                )}
              </Space>
            </Card>

            {/* Request Details */}
            <Card size="small" title="Request Details">
              <Descriptions column={1} size="small">
                <Descriptions.Item label="Act Number">
                  #{selectedRequest.actNumber}
                </Descriptions.Item>
                <Descriptions.Item label="Date">
                  {formatDate(selectedRequest.date)}
                </Descriptions.Item>
                <Descriptions.Item label="Deadline">
                  {formatDate(selectedRequest.deadline)}
                </Descriptions.Item>
                <Descriptions.Item label="Order Number">
                  {selectedRequest.orderNumber}
                </Descriptions.Item>
                <Descriptions.Item label="Product Name">
                  {selectedRequest.productName}
                </Descriptions.Item>
                <Descriptions.Item label="Reprint Quantity">
                  {selectedRequest.reprintQuantity}
                </Descriptions.Item>
                <Descriptions.Item label="Reason">
                  {getReasonText(selectedRequest.complaintReason, selectedRequest.language)}
                </Descriptions.Item>
                {selectedRequest.otherReasonText && (
                  <Descriptions.Item label="Other Reason">
                    {selectedRequest.otherReasonText}
                  </Descriptions.Item>
                )}
                {selectedRequest.reprintReason && (
                  <Descriptions.Item label="Additional Notes">
                    {selectedRequest.reprintReason}
                  </Descriptions.Item>
                )}
              </Descriptions>
            </Card>

            {/* Order Snapshot */}
            {selectedRequest.orderSnapshot && (
              <Card size="small" title="Original Order Details">
                <Descriptions column={1} size="small">
                  <Descriptions.Item label="Client">
                    {selectedRequest.orderSnapshot.client?.client || "—"}
                  </Descriptions.Item>
                  <Descriptions.Item label="Original Quantity">
                    {selectedRequest.orderSnapshot.labelCount || "—"}
                  </Descriptions.Item>
                  <Descriptions.Item label="Machine">
                    {selectedRequest.orderSnapshot.selectedProduct?.machine || "—"}
                  </Descriptions.Item>
                  <Descriptions.Item label="Material">
                    {selectedRequest.orderSnapshot.selectedProduct?.material || "—"}
                  </Descriptions.Item>
                </Descriptions>
              </Card>
            )}

            {/* Complaint Info */}
            {selectedRequest.complaintInfo && (
              <Card size="small" title="Complaint Information">
                <Descriptions column={1} size="small">
                  <Descriptions.Item label="Type">
                    {selectedRequest.complaintInfo.type}
                  </Descriptions.Item>
                  <Descriptions.Item label="Responsibility">
                    {selectedRequest.complaintInfo.responsibility}
                  </Descriptions.Item>
                  <Descriptions.Item label="Comment">
                    {selectedRequest.complaintInfo.comment}
                  </Descriptions.Item>
                </Descriptions>
              </Card>
            )}
          </Space>
        )}
      </Drawer>

      {/* Approve Modal */}
      <Modal
        title="Approve Reprint Request"
        open={approveModalOpen}
        onOk={handleConfirmApprove}
        onCancel={() => setApproveModalOpen(false)}
        confirmLoading={processing}
        okText="Create Order"
        cancelText="Cancel">
        <Space direction="vertical" style={{ width: "100%" }}>
          <Text>How many items should be reprinted?</Text>
          <InputNumber
            min={1}
            value={reprintQuantity}
            onChange={setReprintQuantity}
            style={{ width: "100%" }}
            addonAfter="pcs"
          />
          <Text type="secondary">
            A new order will be created with order number from the sequence.
          </Text>
        </Space>
      </Modal>
    </>
  )
}
